/**
 * 把「牌桌视图的变化」翻译成动效调用（§11.6）。
 *
 * 为什么是 diff 驱动而不是事件驱动：客户端的真源是 `PublicHandState`（tableStore.view），
 * 三种形态（WS / 本地引擎 / 回放）都往那里写。按视图 diff 意味着**不管谁在推状态**，
 * 动画都成立；事件流只是加速器（`motion.ts` 的时序表给事件队列用），
 * 而不是动画的前置条件。断线重连收到整份快照时也不会重放一堆补间动画——
 * 快照会被当成「新的一手」直接落位（§11.6：收到 roomSnapshot 不播补间）。
 *
 * ★ 红线 #50 在这里落地：只有 `actionOn` 变成 hero 时才 `fastForward()`。
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CardId, HandPhase, PublicHandState, SeatIndex } from '@poker/shared'

import { fxKey, type ChipFlight, type FxController } from './FxController.js'
import { dealTotalMs, MOTION } from './motion.js'
import { playSfx, unlockSfx, type SfxKind } from './sfx.js'
import { playFoleySample } from './sampleSfx.js'

// ============================================================================
// 快照
// ============================================================================

interface FxSnapshot {
  handId: string | null
  phase: HandPhase | null
  committed: Map<SeatIndex, number>
  potTotal: number
  actionOn: SeatIndex | null
  actionCount: number
  uncalledSeat: SeatIndex | null
  settled: boolean
}

function emptySnapshot(): FxSnapshot {
  return {
    handId: null,
    phase: null,
    committed: new Map(),
    potTotal: 0,
    actionOn: null,
    actionCount: 0,
    uncalledSeat: null,
    settled: false,
  }
}

function snapshotOf(view: PublicHandState): FxSnapshot {
  const committed = new Map<SeatIndex, number>()
  for (const p of Object.values(view.players)) committed.set(p.seat, p.committedThisRound)
  return {
    handId: view.handId,
    phase: view.phase,
    committed,
    potTotal: view.potTotal,
    actionOn: view.actionOn,
    actionCount: view.actionLog.length,
    uncalledSeat: view.uncalledReturn?.seat ?? null,
    settled: view.phase === 'complete',
  }
}

function sumOf(m: ReadonlyMap<SeatIndex, number>): number {
  let t = 0
  for (const v of m.values()) t += v
  return t
}

/** 盲注/底注是引擎自动贴的，不算「有人行动过」 */
const FORCED_ACTIONS = new Set(['postSmallBlind', 'postBigBlind', 'postAnte', 'postDead'])

export function hasVoluntaryAction(view: PublicHandState): boolean {
  return view.actionLog.some((a) => !FORCED_ACTIONS.has(a.type))
}

/**
 * 一次新增动作对应的事件音。直接读动作记录，不从最终筹码余额反推：结束一条街的
 * 跟注会在同一帧里把 committedThisRound 清零，余额 diff 会把这次声音稳定地漏掉。
 * actionCount 必须只增加 1；快照一次补进多条历史动作时不重放一串过期音效。
 */
export function newActionSfx(
  actionLog: PublicHandState['actionLog'],
  previousCount: number,
): SfxKind | null {
  if (actionLog.length !== previousCount + 1) return null
  const action = actionLog[actionLog.length - 1]
  if (action === undefined || FORCED_ACTIONS.has(action.type)) return null
  if (action.type === 'check') return 'check'
  return action.delta > 0 ? 'chip' : null
}

/**
 * 发牌顺序：从按钮左手第一位开始顺时针，**逐轮**发（每人一张，再每人一张），
 * 与真实发牌一致。视觉顺序错了会让人觉得「这牌是一把抓给我的」。
 */
export function dealKeyOrder(
  dealtSeats: readonly SeatIndex[],
  buttonSeat: SeatIndex,
  heroSeat: SeatIndex | null,
): string[] {
  const sorted = [...dealtSeats].sort((a, b) => a - b)
  const after = sorted.filter((s) => s > buttonSeat)
  const before = sorted.filter((s) => s <= buttonSeat)
  const order = [...after, ...before]
  const keys: string[] = []
  for (let round = 0; round < 2; round++) {
    for (const seat of order) {
      keys.push(seat === heroSeat ? fxKey.hero(round) : fxKey.hole(seat, round))
    }
  }
  return keys
}

// ============================================================================
// 音效解锁
// ============================================================================

/** `attachSfxUnlock` 需要的最小 DOM 面：真实调用点是 `window`，测试传假实现。 */
export type GestureType = 'pointerdown' | 'pointerup' | 'touchend' | 'click'
export interface GestureSource {
  addEventListener(type: GestureType, fn: () => void, opts?: { once?: boolean; passive?: boolean }): void
  removeEventListener(type: GestureType, fn: () => void): void
}

/**
 * 按下、抬起、click 都挂：鼠标的 pointerdown 就是用户激活，但**触屏的 pointerdown 不一定算**——
 * iOS 要等手指抬起（touchend / pointerup，docs/ARCHITECTURE.md §11.9）。click 也不可靠：
 * iOS 上点在没有点击处理的区域根本不派发 click，牌桌的动作按钮又是 pointerup 触发的。
 * 只挂 pointerdown，iPhone 上可能一直解不了锁。
 * 同一下点击会触发好几个：context 已经 running 时 unlock 只是几次状态比较和缓存命中。
 */
const UNLOCK_GESTURES: readonly GestureType[] = ['pointerdown', 'pointerup', 'touchend', 'click']

/**
 * 挂手势监听解锁音效的 AudioContext（`sfx.ts#unlock` 顶部注释解释了为什么必须
 * 这样接线：本 hook 里所有 `playSfx` 调用都是 diff effect 触发的，不在手势的调用栈里，
 * 直接调 `play()` 建出来的 AudioContext 会永远卡在 suspended、静默无声）。
 *
 * ★ 监听**不能**是 `{ once: true }` 的一次性监听。AudioContext 不是「解锁一次就永远
 *   running」：iOS Safari 在切后台时会把它重新 suspend（Android Chrome 在系统回收
 *   音频焦点时同理），回前台后 `state` 又是 'suspended'，而一次性监听早就自己摘掉了
 *   —— 于是整局剩下的时间彻底无声，正好是这个修复本来要治的那个症状。
 *   第一下 pointerdown 还可能是程序派发的合成事件（`isTrusted === false`），
 *   那一下的 `resume()` 不在真实手势里、静默失败，却把唯一的机会用掉了。
 *   常驻监听没有这两个坑：`unlock()` 在 context 已经 running 时直接返回，
 *   录音那边也只是几次缓存命中，代价是每次点击多几次比较。
 */
export function attachSfxUnlock(target: GestureSource, unlock: () => void = unlockSfx): () => void {
  for (const type of UNLOCK_GESTURES) target.addEventListener(type, unlock, { passive: true })
  return () => {
    for (const type of UNLOCK_GESTURES) target.removeEventListener(type, unlock)
  }
}

// ============================================================================
// 主 hook
// ============================================================================

export interface TableFxInput {
  readonly fx: FxController | null
  readonly view: PublicHandState | null
  readonly heroSeat: SeatIndex | null
  /** 遮牌 / 未挂载时为 false：此时只更新快照，不播任何动画（音效同理） */
  readonly enabled: boolean
  /** 设置里的「音效」开关。省略按关闭处理，旧调用点/测试不用改。 */
  readonly soundEnabled?: boolean
}

export function useTableFx(input: TableFxInput): void {
  const { fx, view, heroSeat, enabled, soundEnabled = false } = input
  const snapRef = useRef<FxSnapshot>(emptySnapshot())
  const timersRef = useRef<number[]>([])

  // 视角切换（传手机换人）必须把在途动画全部取消，否则上一位的筹码还在飞（§11.8 第 10 条）
  useEffect(() => {
    fx?.cancelAll()
  }, [fx, heroSeat])

  // 音效解锁：见 attachSfxUnlock 注释。只在真的会响的时候才挂，省一次没用的监听
  useEffect(() => {
    if (!soundEnabled || typeof window === 'undefined') return undefined
    return attachSfxUnlock(window)
  }, [soundEnabled])

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      for (const t of timers) clearTimeout(t)
      timers.length = 0
    }
  }, [])

  useEffect(() => {
    if (fx === null) return
    const later = (fn: () => void, ms: number): void => {
      if (ms <= 0) {
        fn()
        return
      }
      const id = globalThis.setTimeout(() => {
        fn()
        timersRef.current = timersRef.current.filter((x) => x !== id)
      }, ms)
      timersRef.current.push(id)
    }

    if (view === null) {
      snapRef.current = emptySnapshot()
      fx.cancelAll()
      return
    }

    const prev = snapRef.current
    const next = snapshotOf(view)
    snapRef.current = next

    // 遮牌期间只推进快照：揭开时不该看到一串补播的动画
    if (!enabled) return

    const bb = view.config.bigBlind

    // —— 新的一手：清场 + 发牌 ——
    if (prev.handId !== view.handId) {
      fx.cancelAll()
      // 断线重连/回放跳转拿到的是半场快照，绝不能补一遍发牌动画（§11.6）。
      // 判据不能写成 `actionLog.length === 0`：正常开局时盲注/底注**已经**在日志里了，
      // 那样写等于永远不发牌。真正的判据是「还没有人做出自愿动作、也还没有公共牌」。
      if (view.phase === 'dealing' || (view.board.length === 0 && !hasVoluntaryAction(view))) {
        fx.dealCards(dealKeyOrder(view.dealtSeats, view.buttonSeat, heroSeat))
        // 整手牌一次响，不逐张响（见 sfx.ts 顶部注释）
        playFoleySample('deal', soundEnabled)
      }
      return
    }

    // —— ★ 红线 #50：只有轮到自己才快进 ——
    // 其他座位（尤其 AI 连打）必须让每个动画播完，否则牌桌上永远看不到任何动效。
    if (next.actionOn !== prev.actionOn && next.actionOn !== null && next.actionOn === heroSeat) {
      fx.fastForward()
      playSfx('turn', soundEnabled)
    }

    // —— 行动气泡 ——
    if (next.actionCount > prev.actionCount) {
      const last = view.actionLog[view.actionLog.length - 1]
      if (last !== undefined) {
        fx.showBubble(last.seat)
      }
      const sound = newActionSfx(view.actionLog, prev.actionCount)
      if (sound === 'chip') playFoleySample('chip', soundEnabled)
      // 过牌拟音仍在选材；宁可暂时无声，也不继续播放已被否掉的敲门式录音。
      else if (sound !== null && sound !== 'check') playSfx(sound, soundEnabled)
    }

    // —— 下注筹码飞出 ——
    for (const [seat, amount] of next.committed) {
      const before = prev.committed.get(seat) ?? 0
      if (amount > before) {
        fx.flyBet(seat, amount - before, bb)
      }
    }

    // —— 收池：本街投入全部归零的那一刻 ——
    const prevStreet = sumOf(prev.committed)
    let collecting = false
    if (prevStreet > 0 && sumOf(next.committed) === 0) {
      const flights: ChipFlight[] = []
      for (const [seat, amount] of prev.committed) if (amount > 0) flights.push({ seat, amount })
      fx.collectToPot(flights, bb)
      collecting = true
    }

    // —— 未跟注退还 ——
    if (next.uncalledSeat !== null && prev.uncalledSeat === null && view.uncalledReturn !== null) {
      fx.returnUncalled(view.uncalledReturn.seat, view.uncalledReturn.amount, bb)
    }

    // —— 结算：推池 + 赢家高亮 ——
    if (next.settled && !prev.settled && view.result !== null) {
      const bySeat = new Map<SeatIndex, number>()
      for (const a of view.result.awards) {
        bySeat.set(a.seat, (bySeat.get(a.seat) ?? 0) + a.amount)
      }
      const awards: ChipFlight[] = [...bySeat].map(([seat, amount]) => ({ seat, amount }))
      // 收池和推池同帧发生时要排队，否则筹码会对穿
      later(() => {
        fx.pushPotTo(awards, bb)
        fx.winnerGlow(awards.map((a) => a.seat))
      }, collecting ? MOTION.collect.duration : 0)
    }
  }, [fx, view, heroSeat, enabled])
}

// ============================================================================
// 公共牌揭示
// ============================================================================

export interface BoardReveal {
  /** 已经翻开的张数；`< board.length` 的差额是正在飞入、还盖着的牌 */
  readonly shownCount: number
}

/**
 * 公共牌「飞入 → 翻面」两段式。
 *
 * 为什么必须两段：牌一发出来 `view.board` 里就有它了，若直接按正面渲染，
 * 220ms 的飞入过程中玩家已经看清点数，随后那个 300ms 的 3D 翻牌就变成了纯装饰
 * （还会从正面翻到正面，闪一下）。所以本地维护一个滞后的 `shownCount`：
 * 新牌先以背面飞入，落位后再翻。
 */
export function useBoardReveal(
  board: readonly CardId[],
  fx: FxController | null,
  enabled: boolean,
): BoardReveal {
  const [shownCount, setShownCount] = useState(0)
  const prevShownRef = useRef(0)
  const timerRef = useRef<number>(0)

  useEffect(() => {
    const len = board.length
    // 新的一手（或回到 idle）：牌面收回，计数清零
    if (len < shownCount) {
      setShownCount(len)
      return undefined
    }
    if (len === shownCount) return undefined

    if (!enabled || fx === null) {
      setShownCount(len)
      return undefined
    }

    const keys: string[] = []
    for (let i = shownCount; i < len; i++) keys.push(fxKey.board(i))
    fx.dealCards(keys)

    const wait = dealTotalMs(keys.length)
    if (timerRef.current !== 0) clearTimeout(timerRef.current)
    timerRef.current = globalThis.setTimeout(() => {
      timerRef.current = 0
      setShownCount(len)
    }, wait)

    return () => {
      if (timerRef.current !== 0) {
        clearTimeout(timerRef.current)
        timerRef.current = 0
      }
    }
  }, [board.length, shownCount, fx, enabled])

  // 翻牌必须在「牌面已经按正面渲染」的同一帧启动，否则会从正面翻到正面
  useLayoutEffect(() => {
    const from = prevShownRef.current
    prevShownRef.current = shownCount
    if (fx === null || !enabled || shownCount <= from) return
    const keys: string[] = []
    for (let i = from; i < shownCount; i++) keys.push(fxKey.board(i))
    fx.flipCards(keys)
  }, [shownCount, fx, enabled])

  return { shownCount }
}
